type ClassValue = string | false | null | undefined;

export const classNames = (...values: ClassValue[]): string => (
  values.filter(Boolean).join(' ')
);

type SpaceScale = 0 | 1 | 2 | 3 | 4 | 5;
export type MarginSpace = SpaceScale | 'auto';
export type PaddingSpace = SpaceScale;
export type TextAlign = 'start' | 'center' | 'end';
export type SurfaceBackground =
  | 'white'
  | 'light'
  | 'light-subtle'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'dark'
  | 'body'
  | 'transparent';
export type SurfaceBorderColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'
  | 'white';
export type SurfaceRadius = boolean | 'circle' | 'pill';
export type SurfaceShadow = boolean | 'none' | 'sm' | 'lg';
export type FixedPosition = 'top' | 'bottom';
export type PositionMode = 'relative' | 'absolute' | 'fixed' | 'sticky';
export type PositionInset = 0 | 50 | 100;
export type PositionTranslate = 'middle' | 'middle-x' | 'middle-y';
export type DisplayMode = 'block' | 'contents' | 'inline' | 'inline-block' | 'none';
export type FlexGrow = boolean | 0 | 1;
export type OverflowY = 'auto' | 'hidden' | 'scroll' | 'visible';
export type ContainerMode = 'default' | 'fluid';
export type TextColor =
  | 'body'
  | 'dark'
  | 'danger'
  | 'info'
  | 'light'
  | 'muted'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'white';
export type TextDecoration = 'none';
export type TextFontWeight = 'bold' | 'semibold' | 'normal';
export type TextFontSize = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingVisualLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type HeadingDisplaySize = 1 | 2 | 3 | 4 | 5 | 6;

export interface LayoutClassNameProps {
  margin?: MarginSpace;
  marginTop?: MarginSpace;
  marginEnd?: MarginSpace;
  marginBottom?: MarginSpace;
  marginStart?: MarginSpace;
  marginX?: MarginSpace;
  marginY?: MarginSpace;
  padding?: PaddingSpace;
  paddingTop?: PaddingSpace;
  paddingEnd?: PaddingSpace;
  paddingBottom?: PaddingSpace;
  paddingStart?: PaddingSpace;
  paddingX?: PaddingSpace;
  paddingY?: PaddingSpace;
  textAlign?: TextAlign;
}

export interface SurfaceClassNameProps {
  background?: SurfaceBackground;
  bordered?: boolean;
  borderless?: boolean;
  borderColor?: SurfaceBorderColor;
  rounded?: SurfaceRadius;
  shadow?: SurfaceShadow;
}

export interface PositionClassNameProps {
  bottom?: PositionInset;
  end?: PositionInset;
  fixed?: FixedPosition;
  position?: PositionMode;
  positionTranslate?: PositionTranslate;
  start?: PositionInset;
  top?: PositionInset;
}

export interface FlowClassNameProps {
  display?: DisplayMode;
  flexGrow?: FlexGrow;
  overflowY?: OverflowY;
}

export interface ContainerClassNameProps {
  container?: ContainerMode;
}

export interface TextClassNameProps {
  fontSize?: TextFontSize;
  fontWeight?: TextFontWeight;
  textColor?: TextColor;
  textDecoration?: TextDecoration;
  textReset?: boolean;
}

export interface HeadingClassNameProps {
  displaySize?: HeadingDisplaySize;
  visualLevel?: HeadingVisualLevel;
}

const spacingClass = (
  prefix: 'm' | 'mt' | 'me' | 'mb' | 'ms' | 'mx' | 'my' | 'p' | 'pt' | 'pe' | 'pb' | 'ps' | 'px' | 'py',
  value: MarginSpace | PaddingSpace | undefined,
): string | undefined => (value === undefined ? undefined : `${prefix}-${value}`);

export const layoutClassNames = ({
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
}: LayoutClassNameProps = {}): string => classNames(
  spacingClass('m', margin),
  spacingClass('mt', marginTop),
  spacingClass('me', marginEnd),
  spacingClass('mb', marginBottom),
  spacingClass('ms', marginStart),
  spacingClass('mx', marginX),
  spacingClass('my', marginY),
  spacingClass('p', padding),
  spacingClass('pt', paddingTop),
  spacingClass('pe', paddingEnd),
  spacingClass('pb', paddingBottom),
  spacingClass('ps', paddingStart),
  spacingClass('px', paddingX),
  spacingClass('py', paddingY),
  textAlign ? `text-${textAlign}` : undefined,
);

export const surfaceClassNames = ({
  background,
  bordered,
  borderless,
  borderColor,
  rounded,
  shadow,
}: SurfaceClassNameProps = {}): string => classNames(
  background ? `bg-${background}` : undefined,
  bordered ? 'border' : undefined,
  borderless ? 'border-0' : undefined,
  borderColor ? `border-${borderColor}` : undefined,
  rounded === true ? 'rounded' : undefined,
  rounded === 'circle' ? 'rounded-circle' : undefined,
  rounded === 'pill' ? 'rounded-pill' : undefined,
  shadow === true ? 'shadow' : undefined,
  shadow && shadow !== true && shadow !== 'none' ? `shadow-${shadow}` : undefined,
);

export const positionClassNames = ({
  bottom,
  end,
  fixed,
  position,
  positionTranslate,
  start,
  top,
}: PositionClassNameProps = {}): string => classNames(
  fixed ? `fixed-${fixed}` : undefined,
  position ? `position-${position}` : undefined,
  top !== undefined ? `top-${top}` : undefined,
  end !== undefined ? `end-${end}` : undefined,
  bottom !== undefined ? `bottom-${bottom}` : undefined,
  start !== undefined ? `start-${start}` : undefined,
  positionTranslate ? `translate-${positionTranslate}` : undefined,
);

export const flowClassNames = ({
  display,
  flexGrow,
  overflowY,
}: FlowClassNameProps = {}): string => classNames(
  display ? `d-${display}` : undefined,
  flexGrow === true || flexGrow === 1 ? 'flex-grow-1' : undefined,
  flexGrow === 0 ? 'flex-grow-0' : undefined,
  overflowY ? `overflow-y-${overflowY}` : undefined,
);

export const containerClassNames = ({
  container,
}: ContainerClassNameProps = {}): string => classNames(
  container === 'default' ? 'container' : undefined,
  container === 'fluid' ? 'container-fluid' : undefined,
);

export const textClassNames = ({
  fontSize,
  fontWeight,
  textColor,
  textDecoration,
  textReset,
}: TextClassNameProps = {}): string => classNames(
  fontSize ? `fs-${fontSize}` : undefined,
  fontWeight ? `fw-${fontWeight}` : undefined,
  textColor ? `text-${textColor}` : undefined,
  textDecoration ? `text-decoration-${textDecoration}` : undefined,
  textReset ? 'text-reset' : undefined,
);

export const headingClassNames = ({
  displaySize,
  visualLevel,
}: HeadingClassNameProps = {}): string => classNames(
  visualLevel,
  displaySize ? `display-${displaySize}` : undefined,
);
